import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';

import {
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import {MatToolbar} from '@angular/material/toolbar';
import {TranslatePipe} from '@ngx-translate/core';
import {
  MatMenu,
  MatMenuItem,
  MatMenuTrigger,
} from '@angular/material/menu';
import {MatButton} from '@angular/material/button';
import {MatDivider} from '@angular/material/divider';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {OidcAuthService} from '../../../services/infrastructure/auth/oidc-auth/oidc-auth.service';
import {JwtService} from '../../../services/infrastructure/auth/jwt/jwt.service';
import {AppTranslateService} from '../../../services/infrastructure/page/i18n/translate/app-translate/app-translate.service';
import {UserAclService} from '../../../services/infrastructure/auth/acl/user-acl/user-acl.service';
import {LangService} from '../../../services/infrastructure/page/i18n/lang/lang.service';
import {RedirectService} from '../../../services/infrastructure/page/routing/redirect/redirect.service';
import {Constants} from '../../../common/constants/constants';
import {IframeService} from '../../../services/infrastructure/iframe/iframe.service';
import {IframeAuthService} from '../../../services/infrastructure/auth/iframe-auth/iframe-auth.service';
import {Language} from '../../../common/enums/language.enum';
import {Links} from '../../../common/constants/links';
import {
  IColorSchema,
  injectColorSchema,
} from '../../../services/infrastructure/page/browser/color-schema/inject-color-schema';
import {LibraryAclService} from '../../../services/infrastructure/auth/acl/library-acl/library-acl.service';
import {FeatureAclService} from '../../../services/infrastructure/auth/acl/feature-acl/feature-acl.service';
import {UserAvatarComponent} from '../../logo-avatar/user-avatar/user-avatar.component';
import {LocalStorageService} from '../../../services/infrastructure/page/storage/local-storage/local-storage.service';
import {UserAvatarSize} from '../../../common/enums/user-avatar-size.enum';
import {DeputyService} from '../../../services/domain/reference-data/deputy/deputy.service';
import {DeputyContextService} from '../../../services/infrastructure/auth/deputy-context/deputy-context.service';
import {IDeputy} from '../../../models/deputy.model';
import {DeputyNamePipe} from '../../../pipes/deputy-name/deputy-name.pipe';
import {SpinnerComponent} from '../../basic/spinner/spinner.component';
import {SpinnerSize} from '../../../common/enums/spinner.enum';
import {ExpandIconComponent} from '../../basic/expand-icon/expand-icon.component';
import {IconSize} from '../../../common/enums/icon.enum';
import {ButtonType} from '../../../common/enums/button-type.enum';

interface InsurerMenuItem {
  link: string;
  translationKey: string;
  activeRoute?: string;
}

interface PlatformMemberAttributes {
  href: string | null;
  disabled: boolean;
  tooltipDisabled: boolean;
  tooltip: string;
  tabIndex: number;
}

@Component({
  selector: 'app-header-menu',
  templateUrl: './header-menu.component.html',
  styleUrls: ['./header-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatToolbar,
    RouterLink,
    TranslatePipe,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatButton,
    UserAvatarComponent,
    MatDivider,
    RouterLinkActive,
    MatTooltipModule,
    SpinnerComponent,
    DeputyNamePipe,
    MatIcon,
    ExpandIconComponent,
  ],
})
export class HeaderMenuComponent implements OnInit {

  protected readonly ButtonType: typeof ButtonType = ButtonType;

  protected readonly IconSize: typeof IconSize = IconSize;

  private localStorageService: LocalStorageService = inject(LocalStorageService);

  private appTranslateService: AppTranslateService = inject(AppTranslateService);

  private oidcAuthService = inject(OidcAuthService);

  private jwtService = inject(JwtService);

  private userAclService = inject(UserAclService);

  private libraryAclService = inject(LibraryAclService);

  private featureAclService = inject(FeatureAclService);

  private redirectService = inject(RedirectService);

  protected readonly colorSchema: IColorSchema = injectColorSchema();

  private langService = inject(LangService);

  private iframeService = inject(IframeService);

  private iframeAuthService = inject(IframeAuthService);

  private router = inject(Router);

  private deputyService = inject(DeputyService);

  private deputyContextService = inject(DeputyContextService);

  public defaultLang: string = Language.en;

  public langList: string[] = [];

  public LINK = Links;

  protected readonly platformMemberTooltip = 'front.sng.tooltip.feature_not_unlocked';

  private timedOutCloser: ReturnType<typeof setTimeout> | null = null;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => this.clearTooltipCloser());
  }

  public activeTooltipTrigger: MatMenuTrigger | null = null;

  readonly insurerMenuItems: InsurerMenuItem[] = [
    {
      link: this.LINK.LINK_INSURER_ARCHIVE,
      translationKey: 'front.sng.menu.archive',
    },
    {
      link: this.LINK.LINK_INSURER_SETTINGS,
      translationKey: 'front.sng.title.settings',
    },
    {
      link: this.LINK.LINK_INSURER_MIS,
      translationKey: 'front.sng.title.mis',
      activeRoute: this.LINK.MIS_LINK_URL,
    },
  ];

  protected readonly UserAvatarSize = UserAvatarSize;

  /** Raw translated Smart Subscription tooltip text (may contain HTML entities). */
  private smartSubTooltipRaw = toSignal(
    this.appTranslateService.stream('front.sng.menu.smart_subscriptions_tooltip'),
    {initialValue: ''},
  );

  public decodedSmartSubTooltip = computed(() => this.decodeHtmlEntities(this.smartSubTooltipRaw()));

  public readonly deputies: Signal<IDeputy[]> = this.deputyService.deputies;

  /** Whether the "My deputies" section should be shown. */
  public readonly hasDeputies: Signal<boolean> = computed(() => this.deputyService.deputies().length > 0);

  public readonly deputiesLoading: Signal<boolean> = this.deputyService.loading;

  protected readonly SpinnerSize: typeof SpinnerSize = SpinnerSize;

  public readonly isInDeputyContext: Signal<boolean> = this.deputyContextService.isInDeputyContext;

  ngOnInit(): void {
    this.getLangList();
  }

  /**
   * Method: Fallback trigger for the "My deputies" list when the user menu opens on the call
   * dashboard. The list is preloaded by the dashboard page; `ensureDeputiesLoaded()` is idempotent
   * (loaded once per session, broker companies / employees only — Sobrado admins and insurers are
   * skipped), so this never fires a duplicate request.
   */
  public async loadDeputies(): Promise<void> {
    if (this.isCallDashboardPage()) {
      await this.deputyService.ensureDeputiesLoaded();
    }
  }

  /**
   * Method: Enter the deputed colleague's dashboard context and navigate
   * to the broker call dashboard.
   * @param deputy - The selected deputy.
   */
  public async switchToDeputy(deputy: IDeputy): Promise<void> {
    this.deputyContextService.switchToDeputy(deputy);
    await this.router.navigate([Links.BROKER_CALL_DASHBOARD_LINK_URL]);
  }

  /**
   * Method: Leave the deputed context and return to the user's own dashboard.
   */
  public async goBackToMyDashboard(): Promise<void> {
    this.deputyContextService.clearDeputyContext();
    await this.router.navigate([Links.BROKER_CALL_DASHBOARD_LINK_URL]);
  }

  /**
   * Method: Get list lang
   */
  private getLangList(): void {
    this.langList = this.appTranslateService.langListData;
  }

  /**
   * Method: Get selected lang
   */
  public getSelectedLang(): string {
    return this.iframeService.isIframePage() || this.isErrorPage()
      ? this.appTranslateService.getCurrentAppLanguage()
      : this.appTranslateService.getSelectedLang() || this.defaultLang;
  }

  /**
   * Decode HTML entities used in tooltip translation strings: &#{number};, &quot;
   * @param raw - translation string containing encoded HTML entities
   */
  private decodeHtmlEntities(raw: string): string {
    return raw
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(+code))
      .replace(new RegExp(Constants.HTML_ENTITY_QUOT, 'g'), Constants.DOUBLE_QUOTE);
  }

  /**
   * Method: Change lang in application
   * @param lang
   */
  public async setTransLanguage(lang: string): Promise<void> {
    if (this.isErrorPage()) {
      this.appTranslateService.use(lang);
      return;
    }
    if (this.iframeService.isIframePage()) {
      this.iframeAuthService.setIframeLang(lang);
      this.appTranslateService.use(lang);
      this.langService.updateLanguageInIframe(lang.toUpperCase());
    } else {
      const updatedLang = this.jwtService.getLang() !== lang;
      if (updatedLang) {
        this.appTranslateService.use(lang);
        if (this.jwtService.decodedToken) {
          this.jwtService.decodedToken.account.lang = lang;
          await this.langService.updateLanguage(lang.toUpperCase());
        }
      }
    }

    this.localStorageService.setClearSessionStorageFlag(Constants.SESSION_REFRESH);
  }

  /**
   * Method: Logout current user
   */
  public async logout(): Promise<void> {
    await this.oidcAuthService.logout();
    this.localStorageService.setClearSessionStorageFlag(Constants.SESSION_LOGOUT);
  }

  /**
   * Method: Check Route url
   * @param url
   */
  public checkRouteUrl(url: string): boolean {
    return (
      this.router.url.indexOf(url) > -1
      && this.router.url.indexOf(Links.ADMIN_LINK_URL) === -1
      && this.router.url.indexOf(Links.IFRAME_LINK_URL) === -1
    );
  }

  /**
   * Method: Check error url
   */
  private isErrorPage(): boolean {
    return this.router.url.indexOf(Links.ERROR_LINK_URL) > -1;
  }

  /**
   * Method: Check admin url
   */
  protected checkAdminUrl(): boolean {
    return this.router.url.indexOf(Links.ADMIN_LINK_URL) > -1;
  }

  /**
   * Method: Whether the current page is the broker call dashboard. The deputy
   * switcher and "back to my dashboard" entries are scoped to this page only.
   */
  protected isCallDashboardPage(): boolean {
    return this.router.url.indexOf(Links.BROKER_CALL_DASHBOARD_LINK_URL) > -1;
  }

  /**
   * Method: Redirect link to library
   * @param path
   */
  public redirectLinkToLibrary(path: string): string {
    return `${Links.LIBRARY_LINK_URL}${Constants.SLASH}${path}`;
  }

  /**
   * Method: Get Redirect link
   * @param request
   */
  public getRedirectLink(request: string): string {
    return this.redirectService.getRedirectSobradoLink(request);
  }

  /**
   * Method: Check access token in jwt
   */
  public hasAccessToken(): boolean {
    return this.jwtService.hasAccessToken();
  }

  /**
   * Method: Get name current account
   */
  public getFullName(): string {
    return this.jwtService.getFullName();
  }

  /**
   * Method: Get role current account
   */
  public getCurrentRoleName(): string {
    return this.userAclService.getCurrentRoleName();
  }

  /**
   * Method: Get string url for redirect to getmorebrain page
   */
  public getRedirectToGetMoreBrain(): string | null {
    return this.hasSmartSubscription() ? this.redirectService.getRedirectToGetMoreBrain() : null;
  }

  /**
   * Method: Check insurer type
   */
  isInsurerRole(): boolean {
    return this.userAclService.isInsurerRole();
  }

  /**
   * Method: Check insurer admin access
   */
  public isInsurerAdmin(): boolean {
    return this.userAclService.isInsurerAdmin();
  }

  /**
   * Method: Check broker admin access
   */
  public isBrokerAdmin(): boolean {
    return this.userAclService.isBrokerAdmin();
  }

  /**
   * Method: Checks if the current user is an application admin.
   * @returns {boolean} True if the user is an admin, false otherwise.
   */
  private isApplicationAdmin(): boolean {
    return this.userAclService.isApplicationAdmin();
  }

  /**
   * Method: Checks if the current user is a library admin.
   * @returns {boolean} True if the user is a library admin, false otherwise.
   */
  public isLibraryAdmin(): boolean {
    return this.userAclService.isLibraryAdmin();
  }

  /**
   * Method: Checks if the user is an application admin or a library admin.
   * @returns {boolean} - True if the user is an application admin or a library admin, false otherwise.
   */
  public isApplicationAdminOrLibraryAdmin(): boolean {
    return this.isApplicationAdmin() || this.isLibraryAdmin();
  }

  /**
   * Method: Check access to tab Inter-Pension in Library
   */
  public hasInterPensionAccess(): boolean {
    return this.libraryAclService.hasInterPensionAccess();
  }

  /**
   * Method: Check Broker type
   */
  public isBrokerRole(): boolean {
    return this.userAclService.isBrokerRole();
  }

  /**
   * Method: Check Guest type
   */
  public isGuestRole(): boolean {
    return this.userAclService.isGuestRole();
  }

  /**
   * Method: Check access for tab product management
   */
  public hasTabProductManagement(): boolean {
    return this.userAclService.isContentCreatorRole();
  }

  /**
   * Method: Check iframe pages
   */
  public isIframePage(): boolean {
    return this.iframeService.isIframePage();
  }

  /**
   * Method: Check access to tab Factsheet
   */
  public isFactsheetEnabled(): boolean {
    return this.libraryAclService.isFactsheetEnabled();
  }

  /**
   * Method: Check access to tab Pension Report
   */
  public isPensionReportEnabled(): boolean {
    return this.libraryAclService.isPensionReportEnabled();
  }

  /**
   * Method: Checks if CNG - Call New Generation feature is enabled for the company.
   * @returns {boolean} True if CNG feature is enabled, false otherwise.
   */
  public isCngEnabled(): boolean {
    return this.featureAclService.isCngEnabled();
  }

  /**
   * Method: Check the ops toggle that replaces the PHP entry point with the SNG dashboard
   */
  public hasEntrypointDashboardOpsAccess(): boolean {
    return this.featureAclService.hasEntrypointDashboardOpsAccess();
  }

  /**
   * Method: Check library access
   */
  public hasInsurerLibraryAccess(): boolean {
    return this.libraryAclService.hasInsurerLibraryAccess();
  }

  /**
   * Method: Check feature access to Smart Subscription
   */
  public hasSmartSubscription(): boolean {
    return !!this.featureAclService.hasSmartSubscription();
  }

  /**
   * Method: Check access to tab Smart Subscription
   */
  public get smartButtonTabIndex(): number {
    return this.hasSmartSubscription() ? 0 : -1;
  }

  /**
   * Computed: Check if user is Broker Platform Member (cached getter)
   */
  protected get isBrokerPlatformMember(): boolean {
    return this.userAclService.isBrokerPlatformMember();
  }

  /**
   * Computed: Check if user is Insurer Platform Member (cached getter)
   */
  protected get isInsurerPlatformMember(): boolean {
    return this.userAclService.isInsurerPlatformMember();
  }

  /**
   * Computed: Check if user is Platform Member (cached getter)
   */
  protected get isPlatformMember(): boolean {
    return this.userAclService.isPlatformMember();
  }

  /**
   * Computed: Get tabIndex for Platform Members (cached getter)
   */
  protected get tabIndex(): number {
    return this.isPlatformMember ? -1 : 0;
  }

  /**
   * Helper: Get common platform member attributes for menu items
   * Reduces template duplication by centralizing platform member logic
   * @param isPlatformMember - whether user is platform member
   * @param link - link to redirect to (optional)
   */
  protected getPlatformMemberAttrs(isPlatformMember: boolean, link?: string): PlatformMemberAttributes {
    return {
      href: !isPlatformMember && link ? this.getRedirectLink(link) : null,
      disabled: isPlatformMember,
      tooltipDisabled: !isPlatformMember,
      tooltip: this.platformMemberTooltip,
      tabIndex: this.tabIndex,
    };
  }

  /**
   * Helper: Get the MIS menu-item attributes. On top of the Platform Member lock, MIS is disabled
   * for a Broker Employee whose company has neither Benchmarking nor Industry Comparison enabled —
   * those two are the only MIS tabs a Broker Employee can reach. No other role is affected.
   */
  protected getMisAttrs(): PlatformMemberAttributes {
    const misAttrs = this.getPlatformMemberAttrs(this.isBrokerPlatformMember, this.LINK.MIS_LINK_URL);
    if (!this.isMisDisabledForBrokerEmployee()) {
      return misAttrs;
    }
    return {
      ...misAttrs,
      href: null,
      disabled: true,
      tooltipDisabled: false,
      tabIndex: -1,
    };
  }

  /**
   * Method: Check whether the Broker Employee has no MIS tab available at all — neither the
   * Benchmarking nor the Industry Comparison feature is enabled for its company.
   */
  private isMisDisabledForBrokerEmployee(): boolean {
    return this.userAclService.isBrokerEmployee()
      && !this.userAclService.isApplicationAdmin()
      && !this.featureAclService.hasAnyMisFeature();
  }

  /**
   * Open the tooltip menu and cancel any pending close.
   * @param trigger - mat-menu trigger that owns the tooltip overlay
   */
  public onTooltipMouseEnter(trigger: MatMenuTrigger): void {
    this.clearTooltipCloser();
    this.activeTooltipTrigger = trigger;
    trigger.openMenu();
  }

  /**
   * Method: Cancel a pending tooltip close so the timer cannot fire against a
   * torn-down trigger.
   */
  private clearTooltipCloser(): void {
    if (this.timedOutCloser) {
      clearTimeout(this.timedOutCloser);
      this.timedOutCloser = null;
    }
  }

  /**
   * Schedule a debounced close of the tooltip menu.
   * @param trigger - mat-menu trigger that owns the tooltip overlay
   */
  public onTooltipMouseLeave(trigger: MatMenuTrigger): void {
    this.timedOutCloser = setTimeout(() => {
      this.timedOutCloser = null;
      trigger.closeMenu();
    }, Constants.DEFAULT_TOOLTIP_DEBOUNCE_TIME);
  }

  /** Keep tooltip open when the cursor moves into its content area. */
  public onTooltipContentMouseEnter(): void {
    if (this.activeTooltipTrigger) {
      this.onTooltipMouseEnter(this.activeTooltipTrigger);
    }
  }

  /** Start debounced close when the cursor leaves the tooltip content. */
  public onTooltipContentMouseLeave(): void {
    if (this.activeTooltipTrigger) {
      this.onTooltipMouseLeave(this.activeTooltipTrigger);
    }
  }

}
